import { useState } from "react";
import { Plus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, Chapter } from "./shared";

const faqs = [
  {
    q: "What is a Source Code?",
    a: "Your Source Code is your personal rhythm profile, not software or programming. It is built once from your exact birth date, time and place, and it names how you start, how you recover and how you land. Each day gets read against it, so you know your peak hours, your dip, and the best window for the thing that matters. Direction, not prediction.",
  },
  {
    q: "Is this based on real science?",
    a: "Circadian biology is real, peer reviewed and Nobel recognised. Your body runs a roughly 24 hour clock that drives cortisol, melatonin, core temperature and alertness, and time of day measurably changes how you decide. That is the ground we build on. How we turn your baseline into a daily score and a window is our own framework: a tool for reflection and timing, not a prediction of outcomes.",
  },
  {
    q: "Why does my score stay the same all day?",
    a: "By design. One day, one verdict. A score that changes every refresh is a slot machine.",
  },
  {
    q: "Do I need my exact birth time?",
    a: "No. Date and place give you a working baseline. Add the time and the read gets sharper.",
  },
  {
    q: "Do the people I add know?",
    a: "No. Nothing is sent to them unless you share a card yourself, and cards never carry birth data.",
  },
  {
    q: "What does it cost?",
    a: "$17.99 a month, or $99 a year, which comes out to about $8.25 a month. Every plan opens with a 3-day free trial, so you can read a few real days before it bills. Cancel any time in Settings.",
  },
];

export const Faq = () => {
  const [open, setOpen] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <section id="faq" className="py-16 sm:py-24 lg:py-32" data-testid="faq-section">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          <Chapter num="11" label="Questions" />
        </Reveal>
        <Reveal>
          <h2 className="h-heavy text-[32px] sm:text-5xl lg:text-6xl leading-[0.95] mb-10 sm:mb-14" data-testid="faq-headline">
            Straight answers.
            <br />
            <span className="text-[#6E7178]">No incense.</span>
          </h2>
        </Reveal>
        <div>
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.06}>
                <div className="border-t border-[#F2F0EB]/10 last:border-b">
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-4 sm:gap-6 py-5 sm:py-6 text-left group"
                    data-testid={`faq-question-${i + 1}`}
                    aria-expanded={isOpen}
                  >
                    <span className="flex items-baseline gap-3 sm:gap-5">
                      <span className="font-mono-brand text-[10px] sm:text-xs text-[#FF4D0A] shrink-0">/{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-display font-bold text-base sm:text-lg md:text-xl text-[#F2F0EB] group-hover:text-[#FF4D0A] transition-colors leading-snug">
                        {f.q}
                      </span>
                    </span>
                    <Plus
                      size={18}
                      className={`shrink-0 text-[#6E7178] transition-transform duration-300 ${isOpen ? "rotate-45 text-[#FF4D0A]" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                        data-testid={`faq-answer-${i + 1}`}
                      >
                        <p className="pb-7 pl-11 text-[#9DA1A8] leading-relaxed max-w-2xl">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
